import { useState } from 'react';
import { Database, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-toastify';

export function VectorOverviewPanel() {
  const [vectorOverview, setVectorOverview] = useState([]);
  const [showOverview, setShowOverview] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState({});
  const [expandedChunks, setExpandedChunks] = useState({});
  const [loadingOverview, setLoadingOverview] = useState(false);

  const fetchVectorOverview = async () => {
    setLoadingOverview(true);
    try {
      const res = await fetch('/api/admin/vector-overview');
      if (!res.ok) throw new Error('Fout bij ophalen vectoroverzicht');
      const data = await res.json();

      console.log("📦 Gehele vectorOverview ontvangen:", data);

      data.forEach((item) => {
        const src = item?.main_chunk?.source;
        if (!src) {
          console.warn("⚠️ Geen 'source' gevonden voor:", item.main_chunk.heading, item.main_chunk.id);
        }
      });

      setVectorOverview(data);
    } catch (err) {
      toast.error('Fout bij laden van vectoroverzicht');
      console.error('❌ Fout bij laden van vectoroverzicht:', err);
    } finally {
      setLoadingOverview(false);
    }
  };

  const toggleOverview = () => {
    if (!showOverview) fetchVectorOverview();
    setShowOverview(!showOverview);
  };

  const toggleFile = (filename) => {
    setExpandedFiles((prev) => ({ ...prev, [filename]: !prev[filename] }));
  };

  const toggleChunk = (chunkId) => {
    setExpandedChunks((prev) => ({ ...prev, [chunkId]: !prev[chunkId] }));
  };

  const groupByFile = (items) => {
    return items.reduce((acc, item) => {
      const source = item?.main_chunk?.source;
      const file = source || 'onbekend';

      if (!source) {
        console.warn("⚠️ Geen source gevonden voor chunk:", item.main_chunk.heading, item.main_chunk.id);
      }

      if (!acc[file]) acc[file] = [];
      acc[file].push(item);
      return acc;
    }, {});
  };

  const sortByHeading = (chunks) => {
    const parseKey = (heading) => {
      const match = heading.match(/^(B)?(\d+(?:\.\d+)*)/);
      if (!match) return [1, [Infinity]];
      const isBijlage = match[1] === 'B';
      const nummerReeks = match[2].split('.').map(n => parseInt(n, 10));
      return [isBijlage ? 1 : 0, nummerReeks];
    };

    return [...chunks].sort((a, b) => {
      const [bijlageA, numsA] = parseKey(a.main_chunk.heading);
      const [bijlageB, numsB] = parseKey(b.main_chunk.heading);

      if (bijlageA !== bijlageB) return bijlageA - bijlageB;
      for (let i = 0; i < Math.max(numsA.length, numsB.length); i++) {
        const na = numsA[i] ?? 0;
        const nb = numsB[i] ?? 0;
        if (na !== nb) return na - nb;
      }
      return 0;
    });
  };

  return (
    <div className="bg-gray-50 dark:bg-dark-800/50 rounded-xl p-6 border border-gray-200 dark:border-dark-700">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Database className="h-5 w-5" /> Vector Database Overzicht
        </h2>
        <button
          onClick={toggleOverview}
          className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          {showOverview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {showOverview ? 'Verberg' : 'Toon'} overzicht
        </button>
      </div>

      {showOverview && (
        <div className="space-y-4 max-h-[700px] overflow-y-auto pr-2">
          {loadingOverview ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Laden...</p>
          ) : (
            Object.entries(groupByFile(vectorOverview)).map(([filename, chunks]) => {
              const totalMain = chunks.length;
              const totalSub = chunks.reduce((sum, item) => sum + item.search_chunks.length, 0);

              return (
                <div key={filename} className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg">
                  <button
                    onClick={() => toggleFile(filename)}
                    className="w-full text-left p-4 font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-700 flex justify-between items-center"
                  >
                    <span>{filename}</span>
                    <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                      {totalMain} headings, {totalSub} chunks
                    </span>
                  </button>
                  {expandedFiles[filename] && (
                    <div className="p-4 space-y-4">
                      {sortByHeading(chunks).map((item) => (
                        <div key={item.main_chunk.id} className="border-t pt-3">
                          <div className="flex justify-between items-center">
                            <button
                              onClick={() => toggleChunk(item.main_chunk.id)}
                              className="text-left text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {item.main_chunk.heading}
                            </button>
                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                              {item.search_chunks.length} chunks
                            </span>
                          </div>
                          {expandedChunks[item.main_chunk.id] && (
                            <div className="mt-2 ml-4 text-sm text-gray-700 dark:text-gray-300">
                              <div className="mb-2 whitespace-pre-wrap">{item.main_chunk.text}</div>
                              <ul className="list-disc pl-4 space-y-1">
                                {item.search_chunks.map((sc) => (
                                  <li key={sc.id} className="text-gray-500 dark:text-gray-400">
                                    {sc.text}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
